// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the lesson panel, the Khipu player, the Khipu editor and the
 * room's card of the presenter's Khipu.
 *
 * On a desktop window they are side panels like Browse, Tools, Analyze and
 * the Studio (owner, 2026-10-10: every panel gently pushes): at the
 * inline-end edge, sliding in and out on the same time and curve, standing
 * beside whatever is open (`--panel-push-khipu`, globePanelOffset.ts), the
 * globe and the corner buttons gliding aside, the foot clear of the
 * transport and the Tools bar. A side panel stands in its column: it is
 * not dragged.
 *
 * Narrower, it opens in its corner and can be dragged anywhere by its
 * title bar, or moved with the arrow keys from its grip, always kept whole
 * on screen (owner, 2026-10-05, first for the lesson panel; shared with the
 * Khipu player since 2026-10-07). The title bar should be
 * `touch-action: none`, so a finger moves the panel, not the page.
 */

/** From this width the panels are side panels (globePanelOffset.ts's desktop). */
export const SIDE_PANEL_MIN_WIDTH = 769
/** The side panels' event: the globe and the corner buttons step aside (globePanelOffset.ts). */
const SIDE_PANEL_EVENT = 'ayni-side-panel'
const asSidePanel = (): boolean => typeof window !== 'undefined' && window.innerWidth >= SIDE_PANEL_MIN_WIDTH
export const announceSidePanel = (): void => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(SIDE_PANEL_EVENT))
}

/** Whether a panel is shown: in the page, and not on its way out. */
export const isPanelShown = (panel: HTMLElement): boolean => panel.isConnected && !panel.classList.contains('lesson-leaving')

/**
 * Take a side panel away: it slides out (`lesson-leaving`), no longer
 * counted or found by its id, and is removed once out; at once without
 * motion or on a narrow window.
 */
export function leaveSidePanel(panel: HTMLElement): void {
  const still = typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (still || !asSidePanel() || !panel.isConnected) {
    panel.remove()
  } else {
    panel.removeAttribute('id')
    panel.classList.add('lesson-leaving')
    panel.setAttribute('aria-hidden', 'true')
    panel.inert = true
    window.setTimeout(() => panel.remove(), 700)
  }
  announceSidePanel()
}

/**
 * The panel's foot clears the transport and the Tools bar, as Analyze's and the Studio's do.
 * Not while it shares Tools' column: lessonToolsColumn.ts sets its height then, and calls this when they part.
 */
export function fitSidePanel(panel: HTMLElement): void {
  if (document.body.classList.contains('lesson-tools-column') && panel.matches('.lesson-player:not(.khipu-editor):not(.game-panel)')) return
  if (!asSidePanel()) {
    panel.style.maxHeight = ''
    return
  }
  let clearance = 0
  for (const id of ['playback-controls', 'map-controls']) {
    const el = document.getElementById(id)
    if (!el || el.classList.contains('hidden')) continue
    const rect = el.getBoundingClientRect()
    if (rect.height > 0) clearance = Math.max(clearance, window.innerHeight - rect.top)
  }
  const top = panel.getBoundingClientRect().top
  panel.style.maxHeight = clearance > 0 && top >= 0 ? `${Math.max(160, Math.round(window.innerHeight - top - clearance - 8))}px` : ''
}

/** The gap the panel keeps from the window's edges, in px. */
const EDGE = 8
/** How far one arrow-key press moves it (Shift: ten times as far). */
const KEY_STEP = 16

/** Where the panel may stand: wholly inside the window, by its top-left corner. */
export function clampPanel(x: number, y: number, w: number, h: number, vw: number, vh: number): { x: number; y: number } {
  const cx = Math.min(Math.max(EDGE, x), Math.max(EDGE, vw - w - EDGE))
  const cy = Math.min(Math.max(EDGE, y), Math.max(EDGE, vh - h - EDGE))
  return { x: Math.round(cx), y: Math.round(cy) }
}

export interface FloatingPanel {
  /** After the panel changed size: keep a moved panel whole on screen (a side panel: its foot clear). */
  keepOnScreen(): void
  /** Stop listening (the panel is going away). */
  destroy(): void
  /** Stop listening and take the panel away (it slides out as a side panel). */
  leave(): void
}

/**
 * Make `panel` draggable by its `headSelector` (not by the head's buttons,
 * except the one with `gripClass`), and movable by arrow keys from the grip.
 * A moved panel gets the class `moved` and `left`/`top`; while dragged, `carried`.
 */
export function makeFloating(panel: HTMLElement, opts: { headSelector: string; gripClass: string }): FloatingPanel {
  /** Where it was moved (px, top-left), or null while it sits in its corner. */
  let moved: { x: number; y: number } | null = null

  function placeAt(x: number, y: number): void {
    const r = panel.getBoundingClientRect()
    // Out of its corner it keeps the width it had there (on a phone the
    // corner sets it by both edges, which a moved panel no longer has).
    if (!moved && r.width > 0) panel.style.width = `${Math.round(r.width)}px`
    moved = clampPanel(x, y, r.width, r.height, window.innerWidth, window.innerHeight)
    panel.classList.add('moved')
    panel.style.left = `${moved.x}px`
    panel.style.top = `${moved.y}px`
  }

  const onPointerDown = (e: PointerEvent) => {
    const target = e.target as HTMLElement
    // A side panel stands in its column.
    if (asSidePanel()) return
    if (e.button > 0 || !target.closest(opts.headSelector)) return
    const button = target.closest('button')
    if (button && !button.classList.contains(opts.gripClass)) return
    e.preventDefault()
    const r = panel.getBoundingClientRect()
    const grabX = e.clientX - r.left
    const grabY = e.clientY - r.top
    panel.classList.add('carried')
    const move = (ev: PointerEvent) => {
      if (ev.pointerId === e.pointerId) placeAt(ev.clientX - grabX, ev.clientY - grabY)
    }
    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== e.pointerId) return
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', end)
      document.removeEventListener('pointercancel', end)
      panel.classList.remove('carried')
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', end)
    document.addEventListener('pointercancel', end)
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (asSidePanel() || !(e.target as HTMLElement).classList.contains(opts.gripClass)) return
    const d = ({ ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] } as Record<string, [number, number]>)[e.key]
    if (!d) return
    e.preventDefault()
    const step = e.shiftKey ? KEY_STEP * 10 : KEY_STEP
    const r = panel.getBoundingClientRect()
    const from = moved ?? { x: r.left, y: r.top }
    placeAt(from.x + d[0] * step, from.y + d[1] * step)
  }

  // A smaller window, or the panel growing, keeps it on screen; a window
  // wide enough for side panels takes a moved panel back to its column.
  const keepOnScreen = () => {
    if (asSidePanel() && moved) {
      moved = null
      panel.classList.remove('moved')
      panel.style.left = panel.style.top = panel.style.width = ''
    }
    if (moved) placeAt(moved.x, moved.y)
    fitSidePanel(panel)
  }

  panel.addEventListener('pointerdown', onPointerDown)
  panel.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', keepOnScreen)
  fitSidePanel(panel)
  announceSidePanel()
  const destroy = () => window.removeEventListener('resize', keepOnScreen)
  return {
    keepOnScreen,
    destroy,
    leave: () => {
      destroy()
      leaveSidePanel(panel)
    },
  }
}
